import {
  HandleGetEntityUpdateJobStatusResponse,
  ModelEntityReferenceForComputeJobs,
} from '@upright/common/internal/jobs/api-types';
import { ModelEntityType } from '@upright/common/model/input/types';
import { safeAssert } from '@upright/common/safe-assert';
import { useGetLoggedInUser, useIsTeam } from '@upright/ui/common/generic/hooks';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchOrThrow } from '../../common/fetchers/fetch-or-throw';
import { JobStatus } from '@upright/common/internal/jobs/types';

async function fetchEntityUpdateJobStatus(entityRef: ModelEntityReferenceForComputeJobs) {
  return await fetchOrThrow<HandleGetEntityUpdateJobStatusResponse>(
    `/entity-update-job-status/${entityRef.type}/${entityRef.id}`,
  );
}

const baseQueryKey = ['getEntityUpdateJobStatus'];

export const getEntityUpdateJobStatusQuery = (entityRef: ModelEntityReferenceForComputeJobs | undefined) =>
  queryOptions({
    queryKey: entityRef ? [...baseQueryKey, entityRef.id, entityRef.type] : baseQueryKey,
    queryFn: async () => {
      safeAssert(!!entityRef, 'Unexpectedly fired query without entity reference.');
      return await fetchEntityUpdateJobStatus(entityRef);
    },
  });

/**
 * Polls for the job status of an entity when there is an ongoing job.
 *  */
export function useGetEntityUpdateJobStatus(
  entityRef: ModelEntityReferenceForComputeJobs | undefined,
  options?: {
    pollingInterval?: number; // milliseconds
  },
) {
  const pollingInterval = options?.pollingInterval ?? 5_000;

  const user = useGetLoggedInUser();
  const isTeam = useIsTeam();

  const enabled = user && !!entityRef?.id && (entityRef.type === ModelEntityType.COMPANY ? isTeam : true);

  return useQuery({
    ...getEntityUpdateJobStatusQuery(entityRef),
    enabled,
    refetchInterval: data => {
      const result = data.state.data;
      const isEmptyObject = Object.keys(result ?? {}).length === 0 && result != null;
      const jobHasFinished = result?.status === JobStatus.SUCCESS;
      const jobHasErrored = result?.status === JobStatus.ERROR;
      const shouldStopPolling = jobHasFinished || isEmptyObject || jobHasErrored;
      // If the job has concluded, we don't need to poll anymore.
      if (shouldStopPolling) {
        return false;
      }
      return pollingInterval;
    },
  });
}
